// apps/web/src/pages/estoque/estornar-movimento-tinta-dialog.tsx — Fase 13, Plano 35 (EST-14, EST-15; adendo "Estornar pesagem/perda" do UI-SPEC).
// Diálogo destrutivo "Estornar movimento" de uma pesagem ou perda de tinta na linha expandida do lote. O botão vermelho
// (#dc2626) só existe aqui e fica desabilitado sem motivo e durante a requisição (spinner); dispensar é "Voltar". Em erro o
// diálogo permanece aberto com o motivo digitado. A escrita usa apiMutate (CSRF); o servidor revalida permissão e motivo.
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { ConsumoTintaLinha, MovimentoTintaLote } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  MOTIVO_ESTORNO_MOVIMENTO_MAXIMO,
  mensagemErroEstornoMovimento,
  textoConfirmacaoEstornoMovimento,
  textoSucessoEstornoMovimento,
  validarMotivo,
} from '../../lib/estoque/estorno-movimento';

export interface EstornarMovimentoTintaDialogProps {
  /** Movimento a estornar (null enquanto o diálogo está fechado). */
  readonly movimento: MovimentoTintaLote | null;
  /** Linha do lote que contém o movimento (código da tinta e lote interno aparecem no texto). */
  readonly lote: ConsumoTintaLinha;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** Sucesso: a página recarrega os movimentos e a linha do lote e mostra a mensagem (role="status"). */
  readonly aoEstornar: (mensagem: string) => void;
}

export function EstornarMovimentoTintaDialog({ movimento, lote, aberto, aoFechar, aoEstornar }: EstornarMovimentoTintaDialogProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setMotivo('');
      setErro(null);
    }
  }, [aberto, movimento?.movimentoId]);

  const podeConfirmar = movimento !== null && validarMotivo(motivo) === null && !enviando;

  async function confirmar(): Promise<void> {
    if (!movimento || !podeConfirmar) return;
    setErro(null);
    setEnviando(true);
    try {
      const res = await apiMutate(`/api/v1/estoque/movimentos-proprios/${encodeURIComponent(movimento.movimentoId)}/estornar`, {
        method: 'POST',
        body: { motivo: motivo.trim() },
      });
      if (!res.ok) {
        // Erro: o diálogo continua aberto, com o motivo digitado, e a mensagem explica o bloqueio (ex.: rateio já fechado).
        const corpo: unknown = await res.json().catch(() => null);
        setErro(mensagemErroEstornoMovimento({ status: res.status, corpo, movimentoCriadoEm: movimento.criadoEm }));
        return;
      }
      aoEstornar(textoSucessoEstornoMovimento(lote.loteInternoPieltec));
      aoFechar();
    } catch {
      setErro(mensagemErroEstornoMovimento({ status: null }));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando || proximo ? undefined : aoFechar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Estornar movimento</DialogTitle>
          <DialogDescription>
            {movimento
              ? textoConfirmacaoEstornoMovimento({
                  modo: movimento.modo,
                  pesoKg: movimento.pesoKg,
                  tintaCodigo: movimento.tintaCodigo,
                  loteInterno: lote.loteInternoPieltec,
                  criadoEm: movimento.criadoEm,
                  usuarioNome: movimento.usuarioNome,
                })
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="motivo-estornar-movimento-tinta">Motivo (obrigatório)</Label>
            <Textarea
              id="motivo-estornar-movimento-tinta"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={MOTIVO_ESTORNO_MOVIMENTO_MAXIMO}
              disabled={enviando}
              rows={3}
            />
          </div>
          {erro && (
            <p role="alert" className="text-sm text-rose-700">
              {erro}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" variant="destructive" onClick={() => void confirmar()} disabled={!podeConfirmar}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Estornar movimento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
